import { useMemo, useEffect } from "react"
import { addMinutes, startOfDay, isAfter, parseISO, getHours, getMinutes } from "date-fns"
import { useFormContext } from "react-hook-form"
import { Clock, ArrowRight } from "lucide-react"
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import useCalendarConfig from "@/modules/calendar/stores/use-calendar-config"
import getFormattedTime from "@/modules/calendar/utils/get-formatted-time"
import useUserEventSideSheet from "@/modules/calendar/stores/use-user-event-side-sheet"

export default function EventSchedule() {
  const { isDetails, isAddition } = useUserEventSideSheet()
  const { watch, setValue } = useFormContext()
  const { step, format, ranges } = useCalendarConfig()
  const { startDate, endDate, startTime, endTime } = ranges
  const formStartDate = watch("startDate")
  const formEndDate = watch("endDate")

  const timeOptions = useMemo(() => {
    const minHour = getHours(parseISO(startTime))
    const maxHour = getHours(parseISO(endTime))
    const maxMinute = getMinutes(parseISO(endTime))

    return Array.from({ length: (24 * 60) / step }, (_, i) => {
      const time = addMinutes(startOfDay(parseISO(formStartDate)), i * step)

      const hour = getHours(time)
      const minute = getMinutes(time)

      if (hour < minHour || hour > maxHour || (hour === maxHour && minute > maxMinute)) {
        return null
      }

      return {
        value: time.toISOString(),
        label: getFormattedTime(time.toISOString(), format),
      }
    }).filter((time) => !!time)
  }, [step, format, formStartDate, startDate, endDate])

  const filteredEndOptions = useMemo(() => {
    return timeOptions.filter((time) => isAfter(parseISO(time.value), parseISO(formStartDate)))
  }, [formStartDate, timeOptions])

  useEffect(() => {
    if (isAddition) {
      setValue("startDate", timeOptions[0].value)
      setValue("endDate", timeOptions[1].value)
    }
  }, [])

  return (
    <div className="flex w-full items-center justify-start">
      <Popover>
        <PopoverTrigger asChild disabled={isDetails} className="disabled:opacity-100">
          <Button
            variant="outline"
            className="h-auto w-auto max-w-full border-none bg-transparent p-0 hover:bg-transparent"
          >
            <div className="flex w-full items-center justify-start gap-3">
              <Clock className="!size-5 text-gray-500" />
              <div className="flex w-full items-center justify-start gap-1 text-gray-600">
                <span className="text-sm font-normal">Horario:</span>
                <span className="flex items-center justify-start gap-4 text-sm font-semibold first-letter:capitalize">
                  <span className="inline-block rounded-lg p-3 uppercase shadow-[0px_0px_0px_0.5px_rgba(0,0,0,0.05),_0px_0.5px_2.5px_0px_rgba(0,0,0,0.3)]">
                    {getFormattedTime(formStartDate, format)}
                  </span>
                  <ArrowRight className="!size-5 text-gray-500" />
                  <span className="inline-block rounded-lg p-3 uppercase shadow-[0px_0px_0px_0.5px_rgba(0,0,0,0.05),_0px_0.5px_2.5px_0px_rgba(0,0,0,0.3)]">
                    {getFormattedTime(formEndDate, format)}
                  </span>
                </span>
              </div>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent side="bottom" align="end" className="w-64 space-y-4">
          {/* Start Time Selector. */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Hora de Inicio</p>
            <Select
              value={formStartDate}
              onValueChange={(newStartDate) => {
                setValue("startDate", newStartDate)

                if (getHours(parseISO(newStartDate)) > getHours(parseISO(formEndDate))) {
                  const newEndDateIndex =
                    timeOptions.findIndex((option) => option.value === newStartDate) + 1

                  if (newEndDateIndex) {
                    setValue("endDate", timeOptions[newEndDateIndex].value)
                  } else {
                    setValue("endDate", newStartDate)
                  }
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                {timeOptions.map((time) => (
                  <SelectItem
                    key={time.value}
                    value={time.value}
                    disabled={
                      time.value === timeOptions.at(-1)?.value || time.value === formEndDate
                    }
                  >
                    {time.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* End Time Selector. */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Hora de Fin</p>
            <Select
              value={formEndDate}
              onValueChange={(value) => {
                setValue("endDate", value)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar" />
              </SelectTrigger>
              <SelectContent>
                {filteredEndOptions.map((time) => (
                  <SelectItem key={time.value} value={time.value}>
                    {time.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
